import { useEffect, useState } from "react";
import { phoneAuth } from "@/lib/auth-client";
import { saveUserSession, type UserSession } from "@/lib/auth-session";
import { PRODUCT_NAME } from "@benyishiguang/ui-foundation";
import invitation from "@/assets/images/login-invitation.png";
import "./phone-login.css";

export function PhoneLogin({ onLogin }: { onLogin: (session: UserSession) => void }) {
	const [phone, setPhone] = useState("");
	const [code, setCode] = useState("");
	const [pending, setPending] = useState<"send" | "verify" | null>(null);
	const [sentTo, setSentTo] = useState("");
	const [seconds, setSeconds] = useState(0);
	const [error, setError] = useState("");
	useEffect(() => {
		if (!seconds) return;
		const id = setTimeout(() => setSeconds(seconds - 1), 1000);
		return () => clearTimeout(id);
	}, [seconds]);
	const validPhone = /^1[3-9]\d{9}$/u.test(phone);
	const send = async () => {
		if (pending || seconds || !validPhone) return;
		setPending("send");
		setError("");
		try {
			const result = await phoneAuth.sendCode(phone);
			setSeconds(result.retryAfter);
			setSentTo(phone);
		} catch (e) {
			setError(e instanceof Error ? e.message : "发送失败，请重试");
		} finally {
			setPending(null);
		}
	};
	return (
		<section className="phone-login">
			<header className="phone-login-brand">{PRODUCT_NAME}</header>
			<div className="phone-login-story">
				<p className="phone-login-eyebrow">给自己一点成长的空间</p>
				<h1>
					今天，
					<br />
					从一个小练习开始
				</h1>
				<p className="phone-login-description">不必做得很好，愿意开始就好。</p>
				<img
					className="phone-login-invitation"
					src={invitation}
					width={690}
					height={566}
					alt="今日的小练习：留意一件让你感觉不错的小事。3 分钟，给自己一点时间。生活里总有小小的光。"
				/>
			</div>
			<div className="phone-login-entry">
				<h2>手机号登录</h2>
				<p className="phone-login-entry-description">留住每一次小小的成长。</p>
				<form
					onSubmit={async (event) => {
						event.preventDefault();
						if (pending || !validPhone || !/^\d{6}$/u.test(code)) return;
						setPending("verify");
						setError("");
						try {
							const session = await phoneAuth.verify(phone, code);
							saveUserSession(session);
							onLogin(session);
						} catch (e) {
							setError(e instanceof Error ? e.message : "登录失败，请重试");
						} finally {
							setPending(null);
						}
					}}
				>
					<label htmlFor="login-phone">手机号</label>
					<input
						id="login-phone"
						type="tel"
						autoComplete="tel-national"
						inputMode="tel"
						maxLength={11}
						placeholder="请输入手机号"
						value={phone}
						disabled={!!pending}
						onChange={(event) => {
							setPhone(event.target.value.replace(/\D/gu, ""));
							setCode("");
							setError("");
						}}
					/>
					<label htmlFor="login-code">验证码</label>
					<div className="phone-login-code">
						<input
							id="login-code"
							autoComplete="one-time-code"
							inputMode="numeric"
							maxLength={6}
							placeholder="6 位验证码"
							value={code}
							disabled={!!pending}
							onChange={(event) => {
								setCode(event.target.value.replace(/\D/gu, ""));
								setError("");
							}}
						/>
						<button
							type="button"
							disabled={!!pending || seconds > 0 || !validPhone}
							onClick={() => void send()}
						>
							{pending === "send" ? "发送中…" : seconds ? `${seconds} 秒后重发` : "获取验证码"}
						</button>
					</div>
					<div className="phone-login-feedback">
						{error ? (
							<p role="alert">{error}</p>
						) : (
							<p role="status">
								{sentTo && sentTo === phone ? "验证码已发送，5 分钟内有效。" : ""}
							</p>
						)}
					</div>
					<button
						className="phone-login-submit"
						type="submit"
						disabled={!!pending || !validPhone || !/^\d{6}$/u.test(code)}
					>
						{pending === "verify" ? "正在登录…" : "登录，开始练习"}
					</button>
				</form>
				<p className="phone-login-caption">登录后开始练习，头像昵称可稍后设置。</p>
				<small>未注册的手机号验证后将自动创建账号。</small>
			</div>
		</section>
	);
}
